import type { GetOrganizationsForAddressListQuery } from '@/generated/root-types';

/** Default ORDA logo when no reseller logo is stored. */
export const DEFAULT_ORDA_LOGO_SRC =
  "data:image/svg+xml;utf8,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%20280%2060'%3E%3Ctext%20x='0'%20y='45'%20font-size='48'%20font-family='Arial'%20fill='%231a1a1a'%3EORDA%3C/text%3E%3C/svg%3E";

export const MAX_LOGO_FILE_BYTES = 500_000;
export const CUSTOMER_INVITE_EMAIL_TEMPLATE = 'customer-invite-template.html';
export const INVITE_RESELLER_LOGO_CONTENT_ID = 'reseller-logo';

const backofficeRegisterBaseUrl =
  import.meta.env.VITE_BACKOFFICE_REGISTER_URL ?? 'https://dev.backoffice.orda-app.com';

export function buildRegistrationLink(tenantId: string, invitationToken?: string): string {
  const base = backofficeRegisterBaseUrl.replace(/\/$/, '');
  const link = `${base}/register?ref=${tenantId}`;
  return invitationToken ? `${link}&invite=${invitationToken}` : link;
}

export type InviteOrganizationNode = NonNullable<
  NonNullable<GetOrganizationsForAddressListQuery['organizations']>['nodes']
>[number];

export type InviteAddressNode = NonNullable<InviteOrganizationNode['addresses']>[number];

export type CustomerContactForm = {
  line1: string;
  line2: string;
  city: string;
  zip: string;
  country: string;
  phone: string;
  email: string;
};

export const emptyContactForm = (): CustomerContactForm => ({
  line1: '',
  line2: '',
  city: '',
  zip: '',
  country: '',
  phone: '',
  email: '',
});

export function pickCustomerContactAddress(
  addresses: InviteAddressNode[] | null | undefined,
): InviteAddressNode | null {
  const contacts = (addresses ?? []).filter((a) => a.type === 'CUSTOMER_CONTACT');
  return contacts.find((a) => a.isDefault) ?? contacts[0] ?? null;
}

export function contactToForm(address: InviteAddressNode | null): CustomerContactForm {
  if (!address) return emptyContactForm();
  return {
    line1: address.addressLine1 ?? '',
    line2: address.addressLine2 ?? '',
    city: address.city ?? '',
    zip: address.zipCode ?? '',
    country: address.country ?? '',
    phone: address.phoneNumber ?? '',
    email: address.email ?? '',
  };
}

export function formsEqual(a: CustomerContactForm, b: CustomerContactForm): boolean {
  return (
    a.line1 === b.line1 &&
    a.line2 === b.line2 &&
    a.city === b.city &&
    a.zip === b.zip &&
    a.country === b.country &&
    a.phone === b.phone &&
    a.email === b.email
  );
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function contactFormToAddressInput(form: CustomerContactForm) {
  return {
    addressLine1: form.line1 || undefined,
    addressLine2: form.line2 || undefined,
    city: form.city || undefined,
    zipCode: form.zip || undefined,
    country: form.country || undefined,
    phoneNumber: form.phone || undefined,
    email: form.email || undefined,
  };
}

export function dataUriToInlineLogoAttachment(dataUri: string): {
  fileName: string;
  mimeType: string;
  content: string;
  contentId: string;
} | null {
  if (!dataUri.startsWith('data:')) return null;

  const commaIndex = dataUri.indexOf(',');
  if (commaIndex < 0) return null;

  const header = dataUri.slice(5, commaIndex);
  const payload = dataUri.slice(commaIndex + 1);
  const mimeType = header.split(';')[0] || 'image/png';

  let content: string;
  if (header.includes(';base64')) {
    content = payload.replace(/\s/g, '');
  } else {
    const decoded = decodeURIComponent(payload);
    const bytes = new TextEncoder().encode(decoded);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
    content = btoa(binary);
  }

  const ext = mimeType.includes('png')
    ? 'png'
    : mimeType.includes('jpeg') || mimeType.includes('jpg')
      ? 'jpg'
      : mimeType.includes('webp')
        ? 'webp'
        : 'svg';

  return {
    fileName: `reseller-logo.${ext}`,
    mimeType,
    content,
    contentId: INVITE_RESELLER_LOGO_CONTENT_ID,
  };
}

export function buildInviteContactBlock(params: {
  organizationName: string;
  form: CustomerContactForm;
  phoneLabel: string;
  emailLabel: string;
}): string {
  const lines: string[] = [];

  if (params.organizationName.trim()) lines.push(escapeHtml(params.organizationName.trim()));
  if (params.form.line1.trim()) lines.push(escapeHtml(params.form.line1.trim()));
  if (params.form.line2.trim()) lines.push(escapeHtml(params.form.line2.trim()));

  const cityLine = [params.form.zip.trim(), params.form.city.trim()].filter(Boolean).join(' ');
  if (cityLine) lines.push(escapeHtml(cityLine));
  if (params.form.country.trim()) lines.push(escapeHtml(params.form.country.trim()));

  const contactLines: string[] = [];
  if (params.form.phone.trim()) {
    contactLines.push(`${escapeHtml(params.phoneLabel)}: ${escapeHtml(params.form.phone.trim())}`);
  }
  if (params.form.email.trim()) {
    contactLines.push(`${escapeHtml(params.emailLabel)}: ${escapeHtml(params.form.email.trim())}`);
  }

  if (contactLines.length > 0) {
    if (lines.length > 0) lines.push('');
    lines.push(...contactLines);
  }

  return lines.join('<br>');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
